/* Animates the cart drawer's bundle meter.

   The drawer is re-rendered from the Section Rendering API on every cart change, so each
   update hands us a brand-new element that already carries its final value in the markup.
   Left alone the bar would jump straight to the new width, so we keep the last rendered value
   in module scope — this script is only ever evaluated once — and replay the move from there.

   The motion runs through the Web Animations API rather than a CSS transition on purpose. A
   transition needs the element to start at the old value, which means rewinding markup that
   is already correct; if the replay is then throttled (a background tab pauses rAF) the bar
   stays stuck on the rewound number. An animation with no fill mode always resolves back to
   what Liquid rendered, so the worst case is a meter that doesn't animate. */

const FILL_DURATION = 500;
const FILL_EASING = 'cubic-bezier(0.25, 1, 0.5, 1)';
const STEP_DURATION = 280;
const STEP_EASING = 'cubic-bezier(0.34, 1.5, 0.64, 1)';

let lastRenderedRatio = null;

class CartBundleProgress extends HTMLElement {
  connectedCallback() {
    const total = Number(this.dataset.total) || 1;
    const progress = Number(this.dataset.progress) || 0;
    const target = Math.min(Math.max(progress / total, 0), 1);
    const previous = lastRenderedRatio === null ? 0 : lastRenderedRatio;

    lastRenderedRatio = target;

    if (target === previous) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    // A hidden document doesn't advance the animation timeline, so an animation started here
    // would sit frozen on its first keyframe. Nobody is looking; render the final state.
    if (document.hidden) return;

    this.querySelector('.bundle-progress__fill')?.animate(
      [{ width: `${previous * 100}%` }, { width: `${target * 100}%` }],
      { duration: FILL_DURATION, easing: FILL_EASING }
    );

    // Pop each newly reached step as the fill sweeps past it rather than lighting the whole
    // row at once. `backwards` holds the small state through the delay; the step keeps the
    // colour Liquid gave it the whole way, so only the scale is ever in flight.
    this.querySelectorAll('.bundle-progress__step').forEach((step, index) => {
      const stepRatio = (index + 1) / total;
      if (stepRatio <= previous || stepRatio > target) return;

      const isGoal = stepRatio === 1;
      const keyframes = [{ transform: 'translate(-50%, -50%) scale(0.2)', offset: 0 }];
      // The goal overshoots — completing the bundle is the moment worth celebrating.
      if (isGoal) keyframes.push({ transform: 'translate(-50%, -50%) scale(1.35)', offset: 0.6 });
      keyframes.push({ transform: 'translate(-50%, -50%) scale(1)', offset: 1 });

      step.animate(keyframes, {
        duration: isGoal ? STEP_DURATION * 2 : STEP_DURATION,
        delay: ((stepRatio - previous) / (target - previous)) * FILL_DURATION,
        easing: STEP_EASING,
        fill: 'backwards',
      });
    });
  }
}

customElements.define('cart-bundle-progress', CartBundleProgress);
